import axios from "axios";

const runtimeOrigin =
  typeof window !== "undefined" && window.location?.origin
    ? window.location.origin
    : "";

const backendBase =
  (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL.trim()) ||
  runtimeOrigin;

// Endpoint-aware timeout policy:
// - Customer orders/my-links: higher timeout to avoid false failures
// - Admin dashboard GETs: shorter timeout to avoid long UI blocking
// - Bulk operations: very high timeout (async workflows)
const TIMEOUT_DEFAULT_MS = 15000;
const TIMEOUT_LOGIN_MS = 12000;
const TIMEOUT_ADMIN_READ_MS = 10000;
const TIMEOUT_ADMIN_WRITE_MS = 25000;
const TIMEOUT_ORDERS_MS = 60000;
const TIMEOUT_BULK_MS = 300000;

const normalizePath = (url = "") => {
  try {
    let path = "";
    if (/^https?:\/\//i.test(url)) {
      path = new URL(url).pathname || "";
    } else {
      path = String(url).split("?")[0] || "";
    }
    if (!path.startsWith("/")) path = `/${path}`;
    // Normalize possible "/api/..." and "/..." callers
    return path.startsWith("/api/") ? path.slice(4) : path;
  } catch {
    return String(url || "");
  }
};

const getTimeoutForRequest = (url, method = "GET") => {
  const path = normalizePath(url);
  const m = String(method || "GET").toUpperCase();

  // Bulk / long-running operations
  if (
    path === "/orders/bulk" ||
    path.startsWith("/admin/sites/bulk-") ||
    path.startsWith("/admin/sites/refresh-all-connections") ||
    path.startsWith("/admin/bulk-operations")
  ) {
    return TIMEOUT_BULK_MS;
  }

  // Target URL change can queue tens of thousands of connector updates
  if (path === "/my-links/update-target") {
    return TIMEOUT_BULK_MS;
  }

  // Customer order flows - keep generous
  if (
    path === "/orders" ||
    path.startsWith("/orders/") ||
    path.startsWith("/my-links")
  ) {
    return TIMEOUT_ORDERS_MS;
  }

  // "Tum siteleri sec": ~6k site icin ~365KB(gz) tek cevap. Yavas/paketli
  // baglantilarda 15 sn'lik default timeout kesiyordu ("Tum siteleri secerken
  // hata olustu"). Sunucu 200/170ms donuyor; darbogaz WAF->musteri iletimi.
  if (path === "/sites/all-ids") {
    return TIMEOUT_ORDERS_MS;
  }

  if (path === "/auth/login") {
    return TIMEOUT_LOGIN_MS;
  }

  // Admin panel calls - short reads, moderate writes
  if (path.startsWith("/admin/")) {
    return m === "GET" ? TIMEOUT_ADMIN_READ_MS : TIMEOUT_ADMIN_WRITE_MS;
  }

  return TIMEOUT_DEFAULT_MS;
};

const api = axios.create({
  baseURL: `${backendBase}/api`,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: TIMEOUT_DEFAULT_MS,
});

// ============ THREAD 10: REQUEST INTERCEPTOR ============

api.interceptors.request.use(
  (config) => {
    // Dynamic timeout by endpoint unless request explicitly overrides it
    const hasCustomTimeout =
      typeof config.timeout === "number" && config.timeout !== api.defaults.timeout;
    if (!hasCustomTimeout) {
      config.timeout = getTimeoutForRequest(config.url, config.method);
    }

    // Token ekle
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// ============ THREAD 10: RESPONSE INTERCEPTOR ============

api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // 401 - Unauthorized
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      delete api.defaults.headers.common["Authorization"];
      
      const currentPath = window.location.pathname;
      const publicPaths = ["/login", "/register", "/"];
      
      if (!publicPaths.includes(currentPath)) {
        window.location.href = "/login";
      }
    }
    
    // 429 - Rate Limited
    if (error.response?.status === 429) {
      console.warn("Rate limit exceeded:", error.response.data);
    }
    
    // 500+ - Server Error (hassas bilgi sızıntısını önle)
    if (error.response?.status >= 500) {
      console.error("Server error:", error.response?.status);
      // Kullanıcıya gösterilecek mesajı düzenle
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        // Stack trace veya hassas bilgi içeriyorsa gizle
        if (detail.includes("Traceback") || detail.includes("Error:") || detail.length > 200) {
          error.response.data.detail = "Bir hata oluştu. Lütfen daha sonra tekrar deneyin.";
        }
      }
    }
    
    return Promise.reject(error);
  }
);

// ============ THREAD 10: INPUT SANITIZATION HELPERS ============

/**
 * Sanitize input to prevent XSS attacks
 * @param {string} value - Input to sanitize
 * @returns {string} Sanitized input
 */
export const sanitizeInput = (value) => {
  if (!value || typeof value !== "string") return value;
  
  // HTML entities encode
  const htmlEntities = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#x27;",
    "/": "&#x2F;",
  };
  
  return value.replace(/[&<>"'/]/g, (char) => htmlEntities[char]);
};

/**
 * Validate URL format
 * @param {string} url - URL to validate
 * @returns {boolean} Whether URL is valid
 */
export const isValidUrl = (url) => {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    return ["http:", "https:"].includes(parsed.protocol);
  } catch {
    return false;
  }
};

/**
 * Sanitize URL - ensure it starts with http/https
 * @param {string} url - URL to sanitize
 * @returns {string} Sanitized URL
 */
export const sanitizeUrl = (url) => {
  if (!url) return url;
  url = url.trim();
  if (!url.startsWith("http://") && !url.startsWith("https://")) {
    return "https://" + url;
  }
  return url;
};

/**
 * Check if email format is valid
 * @param {string} email - Email to validate
 * @returns {boolean} Whether email is valid
 */
export const isValidEmail = (email) => {
  if (!email) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Truncate text safely (prevent XSS in truncated content)
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated text
 */
export const truncateText = (text, maxLength = 100) => {
  if (!text || typeof text !== "string") return "";
  const sanitized = sanitizeInput(text);
  if (sanitized.length <= maxLength) return sanitized;
  return sanitized.substring(0, maxLength) + "...";
};

export default api;
